// Register ajaxCart component before Alpine starts
// Prefix cart endpoints with the active market root (e.g. "/en-us/") so /cart.js
// returns the localized currency and item URLs. Without this, fetches hit the
// global storefront and the drawer shows GBP / 404s on US-only product handles.
const cartUrl = (path) => {
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  return root.replace(/\/$/, '') + '/' + path.replace(/^\//, '');
};

document.addEventListener('alpine:init', () => {
  Alpine.data('ajaxCart', () => ({
    cart: {
      items: [],
      total_price: 0,
      item_count: 0,
      original_total_price: 0
    },
    isOpen: false,
    isLoading: false,
    freeShippingThreshold: 0,
    currency: 'GBP',
    errorMessage: null,

    // Compare-at-price cache (variant_id -> price in cents)
    compareAtPrices: {},

    // Sample variant IDs (hidden quantity selector)
    sampleVariantIds: new Set(),

    // GWP (Gift With Purchase) state
    gwpConfig: null,
    gwpProcessing: false,
    gwpPendingVariants: new Set(),

    init() {
      // Load GWP config from window
      this.gwpConfig = window.MONPURE_GWP_CONFIG || { enabled: false, tiers: [] };

      // Load initial compare_at_price data from Liquid-rendered JSON
      this.loadCompareAtPrices();
      this.loadSampleVariantIds();

      this.fetchCart();

      // Event Listeners
      window.addEventListener('ajax-cart:add', (event) => {
        this.addToCart(event.detail.formData);
      });

      window.addEventListener('ajax-cart:toggle', () => {
        this.toggle();
      });
      
      window.addEventListener('ajax-cart:open', () => {
        this.open();
      });

      window.addEventListener('cart:updated', () => {
        this.fetchCart();
      });
      
      window.addEventListener('cart:refresh', () => {
        this.fetchCart();
      });

      // Hijack legacy cart links
      document.addEventListener('click', (e) => {
        const cartLink = e.target.closest('a[href="/cart"]');
        if (cartLink) {
          e.preventDefault();
          this.open();
        }
      });
      
      // Close on escape key
      this.$watch('isOpen', (value) => {
        if (value) {
            this.trapFocus();
            document.body.classList.add('cart-drawer-active');
        } else {
            this.releaseFocus();
            document.body.classList.remove('cart-drawer-active');
        }
      });
      
      window.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) {
          this.close();
        }
      });

      // Listen for store-triggered opens
      window.addEventListener('cart-drawer:open', () => this.open());
    },

    updateHeaderCount(count) {
        document.querySelectorAll('.cart-desctop-count-js, .cart-mob-count-js').forEach(el => {
            el.setAttribute('data-count', count);
            const badge = el.querySelector('.cart-count');
            if (badge) badge.textContent = count;
        });
    },

    open() {
      this.isOpen = true;
      // Dispatch for store/other scripts
      window.dispatchEvent(new CustomEvent('cart-drawer:opened'));
    },

    close() {
      this.isOpen = false;
      window.dispatchEvent(new CustomEvent('cart-drawer:closed'));
    },

    toggle() {
      this.isOpen ? this.close() : this.open();
    },

    trapFocus() {
        setTimeout(() => {
            const closeBtn = this.$root.querySelector('.cd-close-btn');
            if (closeBtn) closeBtn.focus();
        }, 300); // Wait for transition
    },
    
    releaseFocus() {},

    loadCompareAtPrices() {
      try {
        const el = document.getElementById('compare-at-prices');
        if (el) {
          const data = JSON.parse(el.textContent);
          this.compareAtPrices = { ...this.compareAtPrices, ...data };
        }
      } catch (e) { /* ignore parse errors */ }
    },

    loadSampleVariantIds() {
      try {
        const el = document.getElementById('sample-variant-ids');
        if (el) {
          const ids = JSON.parse(el.textContent);
          this.sampleVariantIds = new Set(ids);
        }
      } catch (e) { /* ignore parse errors */ }
    },

    isSample(item) {
      return this.sampleVariantIds.has(item.variant_id);
    },

    async refreshCompareAtPrices() {
      try {
        const res = await fetch(`${window.location.pathname}?sections=ajax-cart-drawer`);
        const sections = await res.json();
        if (sections['ajax-cart-drawer']) {
          const temp = document.createElement('div');
          temp.innerHTML = sections['ajax-cart-drawer'];
          const priceEl = temp.querySelector('#compare-at-prices');
          if (priceEl) {
            const data = JSON.parse(priceEl.textContent);
            this.compareAtPrices = data;
          }
          const sampleEl = temp.querySelector('#sample-variant-ids');
          if (sampleEl) {
            const ids = JSON.parse(sampleEl.textContent);
            this.sampleVariantIds = new Set(ids);
          }
        }
      } catch (e) { /* ignore */ }
    },

    async fetchCart() {
      try {
        const response = await fetch(cartUrl('cart.js'));
        const cart = await response.json();
        this.updateFromCart(cart);
        this.refreshCompareAtPrices();
      } catch (error) {
        console.error('Error fetching cart:', error);
      }
    },

    updateFromCart(cart) {
        this.cart = cart;
        this.currency = cart.currency;
        this.updateHeaderCount(cart.item_count);

        if (window.Alpine && Alpine.store('cart')) {
            const store = Alpine.store('cart');
            // Update store data but avoid infinite loops
            store.items = cart.items;
            store.itemCount = cart.item_count;
            store.totalPrice = cart.total_price;
            store.totalDiscount = cart.total_discount || 0;
            store.originalTotalPrice = cart.original_total_price || cart.total_price;
        }

        // Manage GWP gifts after cart update
        this.manageGwpGifts();
    },

    async addToCart(formData) {
      this.isLoading = true;
      this.errorMessage = null;
      try {
        const pairingEl = document.querySelector('[data-product-pairing][data-pairing-active="true"]');
        let response;

        if (pairingEl) {
          const pairingVariantId = pairingEl.getAttribute('data-pairing-variant-id');
          const mainVariantId = formData.get('id');
          const quantity = parseInt(formData.get('quantity'), 10) || 1;
          response = await fetch(cartUrl('cart/add.js'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: [
                { id: parseInt(mainVariantId, 10), quantity: quantity },
                { id: parseInt(pairingVariantId, 10), quantity: 1 }
              ]
            })
          });
        } else {
          response = await fetch(cartUrl('cart/add.js'), {
            method: 'POST',
            body: formData
          });
        }

        if (response.ok) {
          await this.fetchCart();
          this.open();
        } else {
          const data = await response.json();
          this.showError(data.description ? data.description : 'Error adding to cart');
        }
      } catch (error) {
        this.showError('Network error. Please try again.');
      } finally {
        this.isLoading = false;
      }
    },

    async changeItem(key, quantity) {
      this.isLoading = true;
      try {
        const response = await fetch(cartUrl('cart/change.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: quantity })
        });

        const cart = await response.json();
        this.updateFromCart(cart);
      } catch (error) {
        this.fetchCart();
      } finally {
        this.isLoading = false;
      }
    },

    async removeItem(key) {
      await this.changeItem(key, 0);
    },

    compareAtTotal() {
      let total = 0;
      for (const item of this.cart.items || []) {
        const cap = this.compareAtPrices[item.variant_id];
        const spaCap = item.selling_plan_allocation && item.selling_plan_allocation.compare_at_price;
        if (cap && cap > item.final_price) {
          total += cap * item.quantity;
        } else if (spaCap && spaCap > item.final_price) {
          // Subscription line: use the selling plan's RRP so the subtotal markdown reflects the saving
          total += spaCap * item.quantity;
        } else {
          total += item.final_line_price;
        }
      }
      return total;
    },

    money(cents) {
        if (typeof cents !== 'number') return '';
        return (cents / 100).toLocaleString(undefined, {
            style: 'currency',
            currency: this.currency
        }).replace('GBP', '£').replace('USD', '$').replace('EUR', '€');
    },
    
    showError(msg) {
        this.errorMessage = msg;
        setTimeout(() => { this.errorMessage = null; }, 4000);
    },

    // ========== GWP (Gift With Purchase) Methods ==========
    // SKU-based condition rules: a rule qualifies when ANY condition group matches.
    // Within a group, ALL variants must be present with min_quantity in cart.

    /**
     * Get all gift variant IDs from config (GID format)
     */
    getGwpVariantIds() {
      if (!this.gwpConfig?.rules) return new Set();
      return new Set(this.gwpConfig.rules.map(rule => rule.gift_variant_id));
    },

    /**
     * Check if a cart item is an auto-added GWP gift
     */
    isAutoGwpItem(item) {
      const gwpVariantIds = this.getGwpVariantIds();
      if (!gwpVariantIds.has(`gid://shopify/ProductVariant/${item.variant_id}`)) {
        return false;
      }
      return item.properties && item.properties._auto_gwp === 'true';
    },

    /**
     * Check if a cart item is any GWP gift (auto or manual)
     */
    isGwpItem(item) {
      const gwpVariantIds = this.getGwpVariantIds();
      return gwpVariantIds.has(`gid://shopify/ProductVariant/${item.variant_id}`);
    },

    /**
     * Build a map of variant GID → total quantity in cart (excluding auto-added gifts).
     * Used for SKU-based condition matching.
     */
    buildVariantQtyMap() {
      const qtyMap = new Map();
      for (const item of this.cart.items || []) {
        if (this.isAutoGwpItem(item)) continue;
        const gid = `gid://shopify/ProductVariant/${item.variant_id}`;
        qtyMap.set(gid, (qtyMap.get(gid) || 0) + item.quantity);
      }
      return qtyMap;
    },

    /**
     * Non-gift cart subtotal in major currency units (e.g. £, €, $).
     * Mirrors the Rust cart_transform's cart_subtotal and the checkout
     * extension's nonGiftSubtotal — must agree across all three layers,
     * otherwise the gift line will flap as one side adds and another removes.
     */
    nonGiftSubtotal() {
      let pence = 0;
      for (const item of this.cart.items || []) {
        if (this.isAutoGwpItem(item)) continue;
        pence += item.final_line_price != null
          ? item.final_line_price
          : (item.original_line_price || 0);
      }
      return pence / 100;
    },

    /**
     * Get eligible GWP rules. A rule qualifies when EITHER any condition
     * group matches (variant-based) OR the non-gift cart subtotal meets the
     * threshold for the active currency. OR semantics match the Rust
     * cart transform's rule_qualifies() and the checkout UI extension.
     */
    getEligibleRules() {
      if (!this.gwpConfig?.enabled || !this.gwpConfig?.rules) {
        return [];
      }

      const variantQtyMap = this.buildVariantQtyMap();
      const subtotal = this.nonGiftSubtotal();
      const currency = this.currency || 'GBP';

      return this.gwpConfig.rules.filter(rule => {
        if (!rule.enabled) return false;

        const hasGroups = rule.condition_groups && rule.condition_groups.length > 0;
        const threshold = rule.thresholds && rule.thresholds[currency];
        const hasThreshold = typeof threshold === 'number';

        if (!hasGroups && !hasThreshold) return false;

        const groupsPass = hasGroups && rule.condition_groups.some(group =>
          group.variants.every(cv => {
            const qty = variantQtyMap.get(cv.variant_id) || 0;
            return qty >= cv.min_quantity;
          })
        );

        const thresholdPasses = hasThreshold && subtotal >= threshold;

        return groupsPass || thresholdPasses;
      });
    },

    /**
     * Main method to manage GWP gifts - add eligible, remove ineligible.
     * Runs after every cart update to check SKU-based conditions.
     */
    async manageGwpGifts() {
      if (!this.gwpConfig?.enabled || this.gwpProcessing) {
        return;
      }

      const gwpVariantIds = this.getGwpVariantIds();
      if (gwpVariantIds.size === 0) return;

      const eligibleRules = this.getEligibleRules();
      const eligibleVariantIds = new Set(eligibleRules.map(r => r.gift_variant_id));

      // Find auto-added GWP items currently in cart
      const autoGwpItems = (this.cart.items || []).filter(item => this.isAutoGwpItem(item));
      const autoGwpVariantIds = new Set(
        autoGwpItems.map(item => `gid://shopify/ProductVariant/${item.variant_id}`)
      );

      // Clean up pending refs - remove variants now in cart
      for (const variantId of this.gwpPendingVariants) {
        if (autoGwpVariantIds.has(variantId)) {
          this.gwpPendingVariants.delete(variantId);
        }
      }

      // Clear pending for variants no longer eligible
      for (const variantId of this.gwpPendingVariants) {
        if (!eligibleVariantIds.has(variantId)) {
          this.gwpPendingVariants.delete(variantId);
        }
      }

      // Determine variants to ADD (eligible but not in cart and not pending)
      const variantsToAdd = [...eligibleVariantIds].filter(
        variantId => !autoGwpVariantIds.has(variantId) && !this.gwpPendingVariants.has(variantId)
      );

      // Determine items to REMOVE (auto-added but no longer eligible)
      const itemsToRemove = autoGwpItems.filter(
        item => !eligibleVariantIds.has(`gid://shopify/ProductVariant/${item.variant_id}`)
      );

      if (variantsToAdd.length === 0 && itemsToRemove.length === 0) {
        return;
      }

      // Mark variants as pending
      for (const variantId of variantsToAdd) {
        this.gwpPendingVariants.add(variantId);
      }

      this.gwpProcessing = true;

      try {
        await new Promise(resolve => setTimeout(resolve, 250));

        // Add eligible gift variants
        for (const variantId of variantsToAdd) {
          const numericId = variantId.split('/').pop();

          try {
            const response = await fetch(cartUrl('cart/add.js'), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                id: numericId,
                quantity: 1,
                properties: {
                  _auto_gwp: 'true'
                }
              })
            });

            if (!response.ok) {
              console.error('GWP: Failed to add gift', numericId);
              this.gwpPendingVariants.delete(variantId);
            }
          } catch (error) {
            console.error('GWP: Error adding gift', error);
            this.gwpPendingVariants.delete(variantId);
          }
        }

        // Remove ineligible gift items
        for (const item of itemsToRemove) {
          try {
            await fetch(cartUrl('cart/change.js'), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                id: item.key,
                quantity: 0
              })
            });
          } catch (error) {
            console.error('GWP: Error removing gift', error);
          }
        }

        // Refresh cart state without re-triggering manageGwpGifts (avoids loop)
        if (variantsToAdd.length > 0 || itemsToRemove.length > 0) {
          const response = await fetch(cartUrl('cart.js'));
          const cart = await response.json();

          this.cart = cart;
          this.currency = cart.currency;
          this.updateHeaderCount(cart.item_count);
          this.refreshCompareAtPrices();

          if (window.Alpine && Alpine.store('cart')) {
            const store = Alpine.store('cart');
            store.items = cart.items;
            store.itemCount = cart.item_count;
            store.totalPrice = cart.total_price;
            store.totalDiscount = cart.total_discount || 0;
            store.originalTotalPrice = cart.original_total_price || cart.total_price;
          }
        }
      } finally {
        this.gwpProcessing = false;
      }
    }
  }));
});